import {sequencePlayer} from './sequencePlayer.js';
import * as Tone from 'tone';
import { updateProgress, PROGRESS_STAGES } from './utils/progressUtils.js';
import { TIME_CONSTANTS } from './utils/timeUtils.js';

// Константы для Player
const PLAYER_CONFIG = {
    DEFAULT_VOLUME: {
        BACKGROUND_MUSIC: 3,
        NOTES: 0.01,
        MASTER: 1.0
    },
    START_DELAY_SECONDS: 0.1,
    VOLUME_LIMITS: {
        MIN: 0,
        MAX: 10
    },
    LOADING: {
        CHECK_INTERVAL: 50,
        MAX_CHECKS: 200,
        PROGRESS_RANGE: 3
    }
};

class Player {
    constructor() {
        this.isPlaying = false;
        this.currentPlayPromise = null;
        this.backgroundMusicPlayer = null;
        this.musicGainNode = null;
        this.url = null;
        this.loadedUrl = null; // Отслеживаем загруженный URL
        this.volumeSettings = { ...PLAYER_CONFIG.DEFAULT_VOLUME };
    }

    async loadBackgroundMusic(progressCallback = null) {
        try {
            if (!this.url) {
                console.error('URL не установлен для загрузки музыки');
                updateProgress(progressCallback, 0);
                return false;
            }

            const needsReload = this.backgroundMusicPlayer && this.loadedUrl !== this.url;

            if (!this.backgroundMusicPlayer || needsReload) {
                this.disposeOldPlayer();
                await this.createNewPlayer(progressCallback);
                this.loadedUrl = this.url;
            } else {
                updateProgress(progressCallback, PROGRESS_STAGES.COMPLETE);
            }

            return true;
        } catch (error) {
            console.error('Ошибка загрузки фоновой музыки:', error);
            updateProgress(progressCallback, PROGRESS_STAGES.COMPLETE);
            return this.backgroundMusicPlayer?.loaded !== false;
        }
    }

    disposeOldPlayer() {
        if (this.backgroundMusicPlayer) {
            try {
                this.backgroundMusicPlayer.stop();
                this.backgroundMusicPlayer.dispose();
            } catch (e) {
                // Игнорируем ошибки при очистке
            }
        }
    }

    async createNewPlayer(progressCallback) {
        updateProgress(progressCallback, PROGRESS_STAGES.START);
        this.musicGainNode = new Tone.Gain(this.volumeSettings.BACKGROUND_MUSIC);

        const arrayBuffer = await this.loadAudioWithProgress(this.url, progressCallback);
        updateProgress(progressCallback, PROGRESS_STAGES.FILE_LOADED);

        updateProgress(progressCallback, PROGRESS_STAGES.DECODING);
        const decodedBuffer = await this.decodeAudioBuffer(arrayBuffer);

        updateProgress(progressCallback, PROGRESS_STAGES.CREATING_PLAYER);
        this.backgroundMusicPlayer = this.createPlayer(decodedBuffer);

        this.backgroundMusicPlayer.connect(this.musicGainNode);
        this.musicGainNode.toDestination();

        updateProgress(progressCallback, PROGRESS_STAGES.PLAYER_CONNECTED);
        await this.waitForPlayerReady(progressCallback);
    }

    async decodeAudioBuffer(arrayBuffer) {
        const audioContext = Tone.getContext();
        try {
            return await audioContext.decodeAudioData(arrayBuffer.slice(0));
        } catch (decodeError) {
            console.error('Ошибка декодирования аудио:', decodeError);
            return arrayBuffer;
        }
    }

    createPlayer(bufferOrUrl) {
        try {
            return new Tone.Player({
                url: this.url,
                loop: false,
                autostart: false
            });
        } catch (playerError) {
            return new Tone.Player({
                url: bufferOrUrl,
                loop: false,
                autostart: false
            });
        }
    }

    async waitForPlayerReady(progressCallback) {
        return new Promise((resolve) => {
            let resolved = false;

            const checkLoaded = () => {
                const isLoaded = this.backgroundMusicPlayer.loaded === true ||
                                (this.backgroundMusicPlayer.buffer?.loaded === true) ||
                                (this.backgroundMusicPlayer.buffer?.length > 0);
                
                if (isLoaded && !resolved) {
                    resolved = true;
                    updateProgress(progressCallback, PROGRESS_STAGES.COMPLETE);
                    resolve();
                }
                return isLoaded;
            };

            if (checkLoaded()) return;

            this.backgroundMusicPlayer.onload = () => {
                if (!resolved) {
                    resolved = true;
                    updateProgress(progressCallback, PROGRESS_STAGES.COMPLETE);
                    resolve();
                }
            };

            this.backgroundMusicPlayer.onerror = (error) => {
                if (!resolved) {
                    resolved = true;
                    console.error('Ошибка загрузки Player:', error);
                    updateProgress(progressCallback, PROGRESS_STAGES.COMPLETE);
                    resolve();
                }
            };

            let checkCount = 0;
            const { CHECK_INTERVAL, MAX_CHECKS, PROGRESS_RANGE } = PLAYER_CONFIG.LOADING;
            const checkInterval = setInterval(() => {
                checkCount++;
                if (checkLoaded()) {
                    clearInterval(checkInterval);
                    return;
                }
                
                if (!resolved) {
                    const progress = PROGRESS_STAGES.PLAYER_CONNECTED + 
                                   Math.min(PROGRESS_RANGE, (checkCount / MAX_CHECKS) * PROGRESS_RANGE);
                    updateProgress(progressCallback, progress);
                }
                
                if (checkCount >= MAX_CHECKS) {
                    if (!resolved) {
                        resolved = true;
                        clearInterval(checkInterval);
                        updateProgress(progressCallback, PROGRESS_STAGES.COMPLETE);
                        resolve();
                    }
                }
            }, CHECK_INTERVAL);
        });
    }

    async loadAudioWithProgress(url, progressCallback) {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('GET', url, true);
            xhr.responseType = 'arraybuffer';

            xhr.onprogress = (event) => {
                if (event.lengthComputable) {
                    const progressRange = PROGRESS_STAGES.FILE_LOADED - PROGRESS_STAGES.START;
                    const percent = PROGRESS_STAGES.START + (event.loaded / event.total) * progressRange;
                    updateProgress(progressCallback, percent);
                }
            };

            xhr.onload = () => {
                if (xhr.status === 200) {
                    resolve(xhr.response);
                } else {
                    reject(new Error(`Failed to load audio: ${xhr.status}`));
                }
            };

            xhr.onerror = () => {
                reject(new Error('Failed to load audio file'));
            };

            xhr.send();
        });
    }

    setBackgroundMusicVolume(volume) {
        if (!this.musicGainNode) {
            return false;
        }

        // Проверяем, что volume является валидным числом
        if (volume == null || isNaN(volume)) {
            return false;
        }

        this.musicGainNode.gain.value = Math.max(
            PLAYER_CONFIG.VOLUME_LIMITS.MIN,
            Math.min(PLAYER_CONFIG.VOLUME_LIMITS.MAX, Number(volume))
        );
        return true;
    }

    async playMelody() {
        if (this.isPlaying) {
            await this.stopPlayback();
            return;
        }

        try {
            await this.ensureToneReady();
            await this.ensureMusicLoaded();
            this.validatePlayer();

            this.setBackgroundMusicVolume(this.volumeSettings.BACKGROUND_MUSIC);
            this.isPlaying = true;

            const startTime = Tone.now() + Math.max(PLAYER_CONFIG.START_DELAY_SECONDS, TIME_CONSTANTS.MIN_START_DELAY);
            this.startBackgroundMusic(startTime);

            this.currentPlayPromise = sequencePlayer.playFullSequence(startTime);
            await this.currentPlayPromise;
        } catch (error) {
            // Игнорируем ошибки прерывания и ручной остановки - это нормальное поведение
            const isInterrupted = error.name === 'PlaybackInterruptedError' || 
                                 error.message === 'Playback stopped manually';
            if (!isInterrupted) {
                console.error('Ошибка воспроизведения:', error);
                this.isPlaying = false;
                throw error;
            }
            // Для ручной остановки просто завершаем без ошибки
        } finally {
            this.isPlaying = false;
            this.currentPlayPromise = null;
            // Сбрасываем флаг isPlayAllMode после завершения воспроизведения
            // Это важно, чтобы при следующем запуске блока синтезатор и фоновая музыка работали
            if (window.isPlayAllMode) {
                window.isPlayAllMode = false;
                if (window.app && window.app.playAllStats) {
                    window.app.playAllStats.isTracking = false;
                }
            }
        }
    }

    async ensureToneReady() {
        if (Tone.getContext().state !== 'running') {
            await Tone.start();
            await new Promise(resolve => setTimeout(resolve, TIME_CONSTANTS.STABILIZATION_DELAY));
        }
    }

    async ensureMusicLoaded() {
        if (!this.backgroundMusicPlayer) {
            await this.loadBackgroundMusic();
        }
    }

    validatePlayer() {
        if (!this.backgroundMusicPlayer) {
            throw new Error('Player не создан. Не удалось инициализировать воспроизведение.');
        }
    }

    startBackgroundMusic(startTime) {
        try {
            this.backgroundMusicPlayer.start(startTime);
        } catch (startError) {
            console.error('Ошибка при запуске фоновой музыки:', startError);
        }
    }

    async stopPlayback() {
        try {
            this.isPlaying = false;

            if (this.backgroundMusicPlayer) {
                this.backgroundMusicPlayer.stop();
            }

            // Сохраняем ссылку на промис перед остановкой
            const playPromise = this.currentPlayPromise;
            
            // Сбрасываем промис ДО остановки, чтобы не ждать его завершения
            this.currentPlayPromise = null;
            
            // Останавливаем sequencePlayer (это должно прервать выполнение)
            await sequencePlayer.stop();

            // НЕ ждем старый промис - он может продолжаться, но мы уже сбросили ссылку на него
            // Если нужно принудительно прервать, можно добавить короткий таймаут для проверки
            // но не блокируем выполнение
            if (playPromise) {
                // Запускаем проверку в фоне, но не ждем её
                Promise.race([
                    playPromise.catch(() => {}), // Игнорируем ошибки
                    new Promise(resolve => setTimeout(resolve, 100)) // Короткий таймаут
                ]).catch(() => {}); // Игнорируем все ошибки
            }
        } catch (error) {
            console.error('Ошибка остановки:', error);
            // Все равно сбрасываем промис при ошибке
            this.currentPlayPromise = null;
            throw error;
        }
    }

    async setUrl(url) {
        // Если URL изменился, сбрасываем loadedUrl, чтобы загрузить новый файл
        if (this.url !== url) {
            this.url = url;
            this.loadedUrl = null;
        }
    }

}

// Создаем экземпляр и экспортируем функции для обратной совместимости
const player = new Player();

/** Загружает фоновую музыку с отслеживанием прогресса (без запуска воспроизведения) */
export const loadMusicWithProgress = (progressCallback) => player.loadBackgroundMusic(progressCallback);
/** Запускает воспроизведение полной последовательности с фоновой музыкой */
export const playMelody = () => player.playMelody();
/** Останавливает текущее воспроизведение */
export const stopPlayback = () => player.stopPlayback();
/** Устанавливает URL фоновой музыки для воспроизведения */
export const setUrl = (url) => player.setUrl(url);
/** Возвращает состояние воспроизведения */
export const getIsPlaying = () => player.isPlaying;